import { resolvePipeInventoryPrefill } from './pipeInventoryFitterPrefill';
import {
  isPipeInventoryFitterPrefillAllowed,
  lookupPipeInventoryFitterEnd,
} from './pipeInventoryFitterLookup';

export type RunPipeInventoryAddPrefillInput = {
  mode: 'add' | 'edit';
  projectId?: string | number | null;
  projectNumber?: string | null;
  projectName?: string;
  pipeNo?: string | null;
  pipePipeId?: string | null;
  pipeSizesData: Array<Record<string, unknown>>;
};

export type RunPipeInventoryAddPrefillResult =
  | {
      status: 'ok';
      pipeNo: string;
      pipePipeId: string;
      pipeOuterDiameterInches: number;
      pipeWallThicknessInches: number;
      fitterConfirmed: boolean;
    }
  | { status: 'skipped'; reason: string }
  | { status: 'not_found'; reason: string }
  | { status: 'error'; message: string }
  | {
      status: 'missing_setup';
      reason: string;
      pipeOuterDiameterInches: null;
      pipeWallThicknessInches: null;
    };

/**
 * Add-only: confirm Fitter end, then OD/wall from project pipe setup (inches).
 */
export async function runPipeInventoryAddPrefill(
  input: RunPipeInventoryAddPrefillInput
): Promise<RunPipeInventoryAddPrefillResult> {
  if (!isPipeInventoryFitterPrefillAllowed(input.mode)) {
    return { status: 'skipped', reason: 'edit_mode' };
  }

  const projectNumber = String(input.projectNumber ?? '').trim();
  const pipeNo = String(input.pipeNo ?? '').trim();
  const pipePipeId = String(input.pipePipeId ?? '').trim();

  if (!projectNumber && (input.projectId == null || String(input.projectId).trim() === '')) {
    return { status: 'skipped', reason: 'no_project' };
  }
  if (!pipeNo) {
    return { status: 'skipped', reason: 'no_pipe_no' };
  }

  const lookup = await lookupPipeInventoryFitterEnd({
    projectNumber: projectNumber || String(input.projectId),
    projectId: input.projectId,
    pipeNo,
    pipePipeId: pipePipeId || null,
    projectName: input.projectName,
  });

  if (lookup.status === 'error') {
    return { status: 'error', message: lookup.message };
  }
  if (lookup.status === 'not_found') {
    return {
      status: 'not_found',
      reason: `Pipe end "${pipeNo}" was not found in Pipe Fitter for this project.`,
    };
  }

  const prefill = resolvePipeInventoryPrefill({
    projectId: input.projectId,
    projectNumber: projectNumber || String(input.projectId),
    pipeNo,
    pipePipeId,
    pipeSizesData: input.pipeSizesData,
    fitterPipeData: lookup.pipe.pipeData,
  });

  if (prefill.status === 'skip') {
    return { status: 'skipped', reason: prefill.reason };
  }
  if (prefill.status === 'missing_setup') {
    return {
      status: 'missing_setup',
      reason: 'size_not_found',
      pipeOuterDiameterInches: null,
      pipeWallThicknessInches: null,
    };
  }

  return {
    status: 'ok',
    pipeNo: prefill.pipeNo,
    pipePipeId: prefill.pipePipeId,
    pipeOuterDiameterInches: prefill.pipeOuterDiameterInches,
    pipeWallThicknessInches: prefill.pipeWallThicknessInches,
    fitterConfirmed: true,
  };
}
